import React, { useState, useRef } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import { Formik } from 'formik';
import DateTimePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from 'react-datepicker';
import { enGB } from 'date-fns/locale';
import * as Yup from 'yup';
import axios from 'axios';
import { Icon } from '@iconify/react/dist/iconify.js';

import { URL } from '../core/config';
import { useSemester } from '../core/SemesterContext';
import { setCurrentSemester } from '../core/store/actions/semester';
import { hasPermission } from '../core/utils/permissionUtils';
import SemesterService from '../core/services/SemesterService';
import NavHeader from './NavHeader';
import CustomAlert from './CustomAlert';
import Avatar from './Avatar';
import '../styles/Form.css';
import '../styles/responsive.css';
import '../styles/CreateSemester.css';

registerLocale('en-GB', enGB);

const withIcon = (message) => (
  <span style={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
    <Icon icon="f7:exclamationmark-circle-fill" style={{ marginRight: '5px', marginLeft: '24px' }} /> {message}
  </span>
);

function formatVolunteerName(user) {
  if (user.nickname && user.firstLastName) {
    return `${user.nickname} (${user.firstLastName.trim()})`;
  }
  return user.firstLastName?.trim() || user.fullName || 'Unknown User';
}

function CreateSemester() {
  const history = useHistory();
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.profile);
  const canManageSemesters = hasPermission(userProfile, 'MANAGE_SEMESTERS');
  const { currentSemester } = useSemester();

  // Capture the semester that was active *before* this flow started, once, so it
  // can't drift if global state changes mid-flow.
  const sourceSemesterIdRef = useRef(currentSemester?._id || null);

  const [step, setStep] = useState('form');
  const [newSemester, setNewSemester] = useState(null);
  const [alertInfo, setAlertInfo] = useState({ show: false, type: '', message: '' });

  const [volunteers, setVolunteers] = useState([]);
  const [volunteersLoading, setVolunteersLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [importing, setImporting] = useState(false);
  const [activating, setActivating] = useState(false);

  if (!canManageSemesters) {
    return <Redirect to="/main" />;
  }

  const showAlert = (type, message) => setAlertInfo({ show: true, type, message });

  const handleCreateSemester = (values, { setSubmitting }) => {
    SemesterService.createSemester(values)
      .then((created) => {
        setNewSemester(created);
        setSubmitting(false);
        setStep('import');

        const sourceId = sourceSemesterIdRef.current;
        if (sourceId) {
          setVolunteersLoading(true);
          axios
            .get(`${URL}/users/list`, { params: { semester: sourceId, includeSuspended: true } })
            .then((response) => {
              const fetched = response.data || [];
              setVolunteers(fetched);
              setSelectedIds(new Set(fetched.map((user) => user._id)));
            })
            .catch((error) => {
              console.error('Error fetching volunteers from previous semester:', error);
              showAlert('error', 'Could not load volunteers from the previous semester.');
            })
            .finally(() => setVolunteersLoading(false));
        }
      })
      .catch((error) => {
        console.error('Error creating semester:', error);
        showAlert('error', 'There was an error creating the semester. Please try again.');
        setSubmitting(false);
      });
  };

  const toggleVolunteer = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const filteredVolunteers = volunteers.filter((user) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.trim().toLowerCase();
    return (
      (user.fullName || '').toLowerCase().includes(query) ||
      (user.nickname || '').toLowerCase().includes(query)
    );
  });

  const handleImport = () => {
    if (selectedIds.size === 0) {
      setStep('activate');
      return;
    }

    setImporting(true);
    SemesterService.importVolunteers(newSemester._id, Array.from(selectedIds))
      .then(() => {
        showAlert('success', 'Volunteers imported successfully.');
        setStep('activate');
      })
      .catch((error) => {
        console.error('Error importing volunteers:', error);
        showAlert('error', 'There was an error importing volunteers. Please try again.');
      })
      .finally(() => setImporting(false));
  };

  const handleActivate = () => {
    setActivating(true);
    SemesterService.setActiveSemester(newSemester._id)
      .then((activated) => {
        dispatch(setCurrentSemester(activated));
        history.push('/semester-management');
      })
      .catch((error) => {
        console.error('Error activating semester:', error);
        showAlert('error', 'There was an error activating the semester. Please try again.');
        setActivating(false);
      });
  };

  return (
      <div className="page-container">
      <NavHeader />
      <div style={{ textAlign: 'center', marginTop: '10px', display: 'flex', alignItems: 'flex-start'}}>
        <button
          type="button"
          className="button list-button" id="back-button"
          onClick={() => history.push('/semester-management')}
          style={{width: '120px'}}
        >
          <Icon icon="material-symbols:arrow-back" /> Back
        </button>
      </div>
      <h1 className="page-title">Create New Semester</h1>

      {step === 'form' && (
        <div className="row section" style={{ margin: '0', marginBottom: '30px' }}>
          <Formik
            initialValues={{ name: '', startDate: null, endDate: null, type: '' }}
            validationSchema={Yup.object().shape({
              name: Yup.string().required(withIcon('Semester name is required')),
              startDate: Yup.date().nullable().required(withIcon('Start date is required')),
              endDate: Yup.date()
                .nullable()
                .required(withIcon('End date is required'))
                .test('is-after-start', withIcon('End date must be after start date'), function (endDate) {
                  const { startDate } = this.parent;
                  if (!startDate || !endDate) return true;
                  return new Date(endDate) > new Date(startDate);
                }),
              type: Yup.string().oneOf(['Fall', 'Spring']).required(withIcon('Semester type is required')),
            })}
            onSubmit={handleCreateSemester}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
              <Form onSubmit={handleSubmit}>
                <Form.Group controlId="formSemesterName" className="form-group">
                  <label htmlFor="name">Semester Name</label>
                  <input
                    className={`form-input ${touched.name && errors.name ? 'is-invalid' : ''}`}
                    type="text"
                    name="name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    placeholder="e.g. Fall 2026"
                  />
                  {touched.name && errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </Form.Group>

                <Form.Group controlId="formSemesterType" className="form-group">
                  <label htmlFor="type">Semester Type</label>
                  <select
                    className={`form-input ${touched.type && errors.type ? 'is-invalid' : ''}`}
                    name="type"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.type}
                  >
                    <option value="" disabled>Select a type...</option>
                    <option value="Fall">Fall</option>
                    <option value="Spring">Spring</option>
                  </select>
                  {touched.type && errors.type && <div className="invalid-feedback">{errors.type}</div>}
                </Form.Group>

                <Form.Group controlId="formStartDate" className="form-group">
                  <label htmlFor="startDate">Start Date</label>
                  <DateTimePicker
                    selected={values.startDate}
                    onChange={(date) => setFieldValue('startDate', date)}
                    dateFormat="dd/MM/yyyy"
                    locale="en-GB"
                    className={`form-input ${touched.startDate && errors.startDate ? 'is-invalid' : ''}`}
                    wrapperClassName="custom-datepicker-wrapper"
                    name="startDate"
                    popperProps={{ strategy: 'fixed' }}
                    portalId="root-portal"
                  />
                  {touched.startDate && errors.startDate && <div className="invalid-feedback">{errors.startDate}</div>}
                </Form.Group>

                <Form.Group controlId="formEndDate" className="form-group">
                  <label htmlFor="endDate">End Date</label>
                  <DateTimePicker
                    selected={values.endDate}
                    onChange={(date) => setFieldValue('endDate', date)}
                    dateFormat="dd/MM/yyyy"
                    locale="en-GB"
                    className={`form-input ${touched.endDate && errors.endDate ? 'is-invalid' : ''}`}
                    wrapperClassName="custom-datepicker-wrapper"
                    name="endDate"
                    popperProps={{ strategy: 'fixed' }}
                    portalId="root-portal"
                  />
                  {touched.endDate && errors.endDate && <div className="invalid-feedback">{errors.endDate}</div>}
                </Form.Group>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button className="button" disabled={isSubmitting} type="submit">
                    Create Semester
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      {step === 'import' && (
        <div className="semester-import-section">
          <div className="main-descriptions user-title">
            Import Volunteers into "{newSemester?.name}"
          </div>

          {!sourceSemesterIdRef.current ? (
            <div className="semester-import-empty">
              <p>No previous semester found — there is nothing to import.</p>
              <button className="button" onClick={() => setStep('activate')}>Continue</button>
            </div>
          ) : (
            <>
              <div className="semester-import-controls">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search volunteers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="semester-import-bulk-actions">
                  <button
                    type="button"
                    className="list-button"
                    onClick={() => setSelectedIds(new Set(volunteers.map((u) => u._id)))}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="list-button"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Select None
                  </button>
                </div>
              </div>

              {volunteersLoading ? (
                <p>Loading volunteers...</p>
              ) : (
                <ul className="semester-import-list">
                  {filteredVolunteers.length === 0 ? (
                    <li className="semester-import-empty-row">No volunteers found.</li>
                  ) : (
                    filteredVolunteers.map((user) => (
                      <li
                        key={user._id}
                        className={`semester-import-item ${user.suspension?.isSuspended ? 'suspended-user' : ''}`}
                      >
                        <label className={selectedIds.has(user._id) ? 'selected' : ''}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(user._id)}
                            onChange={() => toggleVolunteer(user._id)}
                          />
                          <Avatar user={user} size={32} />
                          <span className="semester-import-item-name">{formatVolunteerName(user)}</span>
                          {user.suspension?.isSuspended && <span className="suspended-badge">Suspended</span>}
                        </label>
                      </li>
                    ))
                  )}
                </ul>
              )}

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button className="button" disabled={importing || volunteersLoading} onClick={handleImport} id="import-members-button">
                  Import Selected ({selectedIds.size})
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'activate' && (
        <div className="semester-activate-section">
          <p>
            Would you like to set "{newSemester?.name}" as the active semester now?
            This will deactivate all other semesters for every user.
          </p>
          <div className="semester-activate-actions">
            <button className="button list-button" disabled={activating} onClick={handleActivate}>
              Set as Active Semester
            </button>
            <button
              type="button"
              className="button list-button"
              disabled={activating}
              onClick={() => history.push('/semester-management')}
            >
              Finish Without Activating
            </button>
          </div>
        </div>
      )}

      {alertInfo.show && (
        <CustomAlert
          type={alertInfo.type}
          message={alertInfo.message}
          autoDismiss={true}
          timeout={4000}
          onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
        />
      )}
      </div>
  );
}

export default CreateSemester;
